import { DeviceResponseType } from "./IDevice";
import { DeviceStateChangedObject } from "./DevicesService";

export class DeviceState<T extends IDeviceStateData> implements IDeviceState<T> {
    _type: string = "";
    _data: T | null = null;
    _typeAsEnum: DeviceResponseType = DeviceResponseType.Unknown;
    _serializedDataWithoutTime: string = "";

    get Data(): T {
        return this._data as T;
    }

    set Data(value: T) {
        this._data = value;
        let { Time, ...justData } = value;
        this._serializedDataWithoutTime = JSON.stringify(justData);
    }

    get Type(): string {
        return this._type;
    }

    set Type(value: string) {
        this._type = value;
    }

    get TypeAsEnum(): DeviceResponseType {
        return this._typeAsEnum;
    }

    set TypeAsEnum(value: DeviceResponseType) {
        this._typeAsEnum = value;
    }

    get StatesComparisonData(): string {
        return this._serializedDataWithoutTime;
    }
}

export interface IDeviceStateData {
    Channel: number;
    StateReliability: StateReliability;
    Time: string;
}

export interface IDeviceState<T extends IDeviceStateData> {
    get Type(): string;
    get TypeAsEnum(): DeviceResponseType;
    get Data(): T;
    set Data(value: T);
    get StatesComparisonData(): string;
}

export enum StateReliability {
    /// <summary>
    /// pewny
    /// </summary>
    Confident = 0,
    /// <summary>
    /// niepewny
    /// </summary>
    Unconfident = 1
}

/// ON/OFF state
export class ChannelOnOffDeviceState extends DeviceState<ChannelOnOffState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.ChannelOnOffState;
        this.Type = "ChannelOnOffState";
        this.Data = state.state as any;
    }
}

export class ChannelOnOffState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: ChannelOnOffStateEnum = ChannelOnOffStateEnum.Unknown;
    Time: string = "";
}

export enum ChannelOnOffStateEnum {
    On = 0,
    Off = 1,
    Unknown = 2
}

export class ChannelHallStateState extends DeviceState<HallState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.HallState;
        this.Type = "IHallState";
        this.Data = state.state as any;
    }
}

export class HallState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Time: string = "";
    State: boolean = false;
}

export class ChannelSceneExecutedState extends DeviceState<SceneExecutedState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.SceneExecuted;
        this.Type = "ISceneExecuted";
        this.Data = state.state as any;
    }
}

export class SceneExecutedState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Time: string = "";
}

/// intercom
export class IntercomDeviceState extends DeviceState<IntercomState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.IntercomState;
        this.Type = "IntercomState";
        this.Data = state.state as any;
    }
}

export class IntercomState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: IntercomStateEnum = IntercomStateEnum.Unknown;
    Time: string = "";
}

export enum IntercomStateEnum {
    Idle = 0,
    IncomingCall = 1,
    AnsweringCall = 2,
    Busy = 3,
    NoAnswerToCall = 4,
    Unknown = 5
}

/// online camera
export class OnlineCameraState extends DeviceState<ChannelOnOffState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.OnlineCamera;
        this.Type = "OnlineCameraState";
        this.Data = state.state as any;
    }
}

/// DoorBellState 
export class DoorBellDeviceState extends DeviceState<DoorBellState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.DoorBellState;
        this.Type = "DoorBellState";
        this.Data = state.state as any;
    }
}

export class DoorBellState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: DoorBellStateEnum = DoorBellStateEnum.Relased;
    Time: string = "";
}

export enum DoorBellStateEnum {
    Pressed = 0,
    Relased = 1,
    PressedAndRelased = 2
}

/// reed position
export class ReedStateDeviceState extends DeviceState<ReedState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.ReedState;
        this.Type = "ReedState";
        this.Data = state.state as any;
    }
}

export class ReedState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: boolean = false;
    Time: string = "";
}

/// blind position
export class BlindPositionDeviceState extends DeviceState<BlindPostionState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.BlindPosition;
        this.Type = "BlindPostionState";
        this.Data = state.state as any;
    }

    public GetBlindStatus(): BlindStatusCode {
        switch (this.Data.Position) {
            case 200:
                return BlindStatusCode.UnableToMove;

            case 201:
                return BlindStatusCode.MotorOverheatedPleaseWait;

            default:
                return BlindStatusCode.Working;
        }
    }
}

export enum BlindStatusCode {
    Unknown = 0,
    Working = 1,
    UnableToMove = 200,
    MotorOverheatedPleaseWait = 201,
}

export class BlindPostionState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Position: number = 0;
    RawPosition: number = 0;
    Time: string = "";
}

/// ButtonState 
export class RemoteButtonDeviceState extends DeviceState<RemoteButtonState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.RemoteButtonState;
        this.Type = "RemoteButtonState";
        this.Data = state.state as any;
    }
}

export class RemoteButtonState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: RemoteButtonStateEnum = RemoteButtonStateEnum.Relased;
    Time: string = "";
}

export enum RemoteButtonStateEnum {
    Pressed = 1,
    Relased = 2,
    PressedAndRelased = 3
}

/// device light color
export class LightColorDeviceState extends DeviceState<LightColorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.LightColor;
        this.Type = "LightColorState";
        this.Data = state.state as any;
    }
}

export class LightColorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    R: number = 0;
    G: number = 0;
    B: number = 0;
    Time: string = "";
}

/// light warmth
export class LightWarmthDeviceState extends DeviceState<LightWarmthState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.LightWarmth;
        this.Type = "LightWarmthState";
        this.Data = state.state as any;
    }
}

export class LightWarmthState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Temperature: number = 0;
    Time: string = "";
}

/// light brightness
export class LightBrightnessDeviceState extends DeviceState<LightBrightnessState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.LightBrightness;
        this.Type = "LightBrightnessState";
        this.Data = state.state as any;
    }
}

export class LightBrightnessState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Brightness: number = 0;
    Time: string = "";
}

/// MeasuredTemperature
export class MeasuredTemperatureDeviceState extends DeviceState<MeasuredTemperatureState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.MeasuredTemperature;
        this.Type = "MeasuredTemperatureState";
        this.Data = state.state as any;
    }
}

export class MeasuredTemperatureState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Temperature: number = 0;
    Type: MeasuredTemperatureTypeEnum = MeasuredTemperatureTypeEnum.InsideTemperature;
    Time: string = "";
}

enum MeasuredTemperatureTypeEnum {
    AirTemperature = 0,
    InsideTemperature = 1,
    OutsideTemperature = 2,
    FloorTemperature = 3,
    SafetyTemperature = 4,
    SetTemperature = 5,
}

/// battery state
export class BatteryDeviceState extends DeviceState<BatteryState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.BatteryState;
        this.Type = "BatteryState";
        this.Data = state.state as any;
    }
}

export class BatteryState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: BatteryStateEnum = BatteryStateEnum.Half;
    Percentage: number = -1;
    Time: string = "";
}

export enum BatteryStateEnum {
    Unknown = -1,
    Full = 0,
    Empty = 1,
    Half = 2,
    Percentage = 3,
    Low = 4,
    ExternalPower = 5
}

/// measured brightness
export class MeasuredBrightnessDeviceState extends DeviceState<MeasuredBrightnessState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.MeasuredBrightness;
        this.Type = "MeasuredBrightnessState";
        this.Data = state.state as any;
    }
}

export class MeasuredBrightnessState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Brightness: number = 0;
    Time: string = "";
}

/// measured energy
export class MeasuredEnergyDeviceState extends DeviceState<MeasuredEnergyState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.MeasuredEnergy;
        this.Type = "MeasuredEnergyState";
        this.Data = state.state as any;
        this.Data.MeasurementParameters = new Map(Object.entries((state.state as any).MeasurementParameters)) as Map<EnergyMeasurementParameter, number>
    }
}

export class MeasuredEnergyState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Time: string = "";
    MeasurementParameters: Map<EnergyMeasurementParameter, number> = new Map<EnergyMeasurementParameter, number>();
}

export enum EnergyMeasurementParameter {
    Voltage = "Voltage",
    Current = "Current",
    PhaseShift = "PhaseShift",
    ActivePower = "ActivePower",
    ReactivePower = "ReactivePower",
    ApparentPower = "ApparentPower",
    PowerFactor = "PowerFactor",
    ActiveForwardEnergy = "ActiveForwardEnergy",
    ActiveReverseEnergy = "ActiveReverseEnergy",
    ApparentEnergy = "ApparentEnergy",
    ReactiveForwardEnergy = "ReactiveForwardEnergy",
    ReactiveReverseEnergy = "ReactiveReverseEnergy",
    Frequency = "Frequency",
    SPIErrorRate = "SPIErrorRate",
    CRCErrorRate = "CRCErrorRate",
    Timestamp = "Timestamp",
    FailedMeasurementsRate = "FailedMeasurementsRate",
    FundamentalActivePower = "FundamentalActivePower",
    HarmonicActivePower = "HarmonicActivePower"
}

/// measured distance
export class MeasuredDistanceDeviceState extends DeviceState<MeasuredDistanceState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.MeasuredDistance;
        this.Type = "MeasuredDistanceState";
        this.Data = state.state as any;
    }
}

export class MeasuredDistanceState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Distance: number = 0;
    DetectedObject: DetectedObjectStateEnum = DetectedObjectStateEnum.No;
    Time: string = "";
}

export enum DetectedObjectStateEnum {
    No = 0,
    Yes = 1,
}

/// movement detection
export class MovementSensorDeviceState extends DeviceState<MovementSensorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.MovementSensorState;
        this.Type = "MovementSensorState";
        this.Data = state.state as any;
    }
}

export class MovementSensorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    /// <summary>
    /// percentage 0% no movement, 100% intense movement
    /// </summary>
    Intensity: number = 0;
    Movement: MovementStateEnum = MovementStateEnum.No;
    Time: string = "";
}

export enum MovementStateEnum {
    No = 0,
    Yes = 1,
}

/// remote blind control button
export class BlindRemoteButtonDeviceState extends DeviceState<BlindRemoteButtonState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.BlindRemoteButtonState;
        this.Type = "BlindRemoteButtonState";
        this.Data = state.state as any;
    }
}

export class BlindRemoteButtonState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: BlindRemoteButtonStateEnum = BlindRemoteButtonStateEnum.StopReleased;
    Time: string = "";
}

export enum BlindRemoteButtonStateEnum {
    OpenPressed = 1,
    OpenReleased = 2,
    StopPressed = 3,
    StopReleased = 4,
    ClosePressed = 5,
    CloseReleased = 6
}

/// remote facade control button
export class FacadeRemoteButtonDeviceState extends DeviceState<FacadeRemoteButtonState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.FacadeRemoteButtonState;
        this.Type = "FacadeRemoteButtonState";
        this.Data = state.state as any;
    }
}

export class FacadeRemoteButtonState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: FacadeRemoteButtonStateEnum = FacadeRemoteButtonStateEnum.StopReleased;
    Time: string = "";
}

export enum FacadeRemoteButtonStateEnum {
    OpenPressed = 1,
    OpenReleased = 2,
    StopPressed = 3,
    StopReleased = 4,
    ClosePressed = 5,
    CloseReleased = 6
}

/// signal strenght
export class SignalStrenghtDeviceState extends DeviceState<SignalStrenghtState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.SignalStrength;
        this.Type = "SignalStrenghtState";
        this.Data = state.state as any;
    }
}

export class SignalStrenghtState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Quality: LinkQualityEnum = LinkQualityEnum.Unknown;
    MeasurementTime: string = "";
    DidLastTaskSucceded: boolean = false;
    Percentage: number = 0;
    SuccededExecutionsPercentage: number = 0;
    SuccededTransmissionsPercentage: number = 0;
    Time: string = "";
}

export enum LinkQualityEnum {
    Unknown = 0,
    Excellent = 1,
    Good = 2,
    Acceptable = 3,
    Low = 4,
    Terrible = 5,
    NoLink = 6
}

/// gate position
export class GatePositionDeviceState extends DeviceState<GatePositionState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.GatePosition;
        this.Type = "GatePositionState";
        this.Data = state.state as any;
    }
}

export class GatePositionState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    PositionType: GatePositionTypeEnum = GatePositionTypeEnum.Unknown;
    Position: number = 0;
    Time: string = "";
}

export enum GatePositionTypeEnum {
    Unknown = 0,
    Open = 1,
    PartiallyOpen = 2,
    Close = 3,
    On = 4,
    Off = 5,
    Percentage = 6
}

/// gateway position
export class GatewayPositionDeviceState extends DeviceState<GatewayPositionState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.GatewayPosition;
        this.Type = "GatewayPositionState";
        this.Data = state.state as any;
    }
}

export class GatewayPositionState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    PositionType: GatewayPositionTypeEnum = GatewayPositionTypeEnum.Unknown;
    Position: number = 0;
    Time: string = "";
}

export enum GatewayPositionTypeEnum {
    Unknown = 0,
    Open = 1,
    PartiallyOpen = 2,
    Close = 3,
    On = 4,
    Off = 5
}

export class GateControllerHealthDeviceState extends DeviceState<GateControllerHealthState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.GateControllerHealth;
        this.Type = "GateControllerHealthState";
        this.Data = state.state as any;
    }
}

export class GateControllerHealthState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Time: string = "";
    ResistanceOrOpticBarStatus: ResistanceOrOpticBarStatusEnum = ResistanceOrOpticBarStatusEnum.Unknown;
    PhotoCellsActive: boolean = false;
    LastAutotestFailed: boolean  = false;
    AutotestSwitchOn: boolean = false;
    ExternalLimitSwitchAActive: boolean = false;
    ExternalLimitSwitchBActive: boolean = false;
    InertionBreaksOk: boolean = false;
    ObstacleDetected: boolean = false;
}

export enum ResistanceOrOpticBarStatusEnum {
    Unknown = -1,
    Inactive = 0,
    Active = 1,
    UnconnectedOrMalfunction = 2
}

/// blind calibration status
export class BlindCalibrationDeviceState extends DeviceState<BlindCalibrationState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.BlindCalibration;
        this.Type = "BlindCalibrationState";
        this.Data = state.state as any;
    }
}

export class BlindCalibrationState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    CalibrationStatus: BlindCalibrationStatusEnum = BlindCalibrationStatusEnum.AutoLernIdle;
    Time: string = "";
}

export enum BlindCalibrationStatusEnum {
    AutoLernIdle = 0x00,
    AutoLernInProgress = 0x01,
    AutoLernSuccessfull = 0x02,
    AutoLernCancelled = 0x04,
}

/// humidity sensor
export class HumiditySensorDeviceState extends DeviceState<HumiditySensorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.HumiditySensorState;
        this.Type = "HumiditySensorState";
        this.Data = state.state as any;
    }
}

export class HumiditySensorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Type: HumidityTypeEnum = HumidityTypeEnum.AirHumidity;
    Humidity: number = 0;
    Time: string = "";
}

export enum HumidityTypeEnum {
    AirHumidity = 0,
    InsideHumidity = 1,
    OutsideHumidity = 2,
    SafetyHumidity = 3,
    SetHumidity = 4,
}


/// Vibration sensor
export class VibrationSensorDeviceState extends DeviceState<VibrationSensorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.VibrationSensorState;
        this.Type = "VibrationSensorState";
        this.Data = state.state as any;
    }
}

export class VibrationSensorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Vibration: VibrationTypeEnum = VibrationTypeEnum.No;
    Acceleration_X: number = 0;
    Acceleration_Y: number = 0;
    Acceleration_Z: number = 0;
    AccelerationModule: number = 0;
    Time: string = "";
}

export enum VibrationTypeEnum {
    No = 0,
    Yes = 1,
}

/// binary sensor
export class BinarySensorStateDeviceState extends DeviceState<BinarySensorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.BinarySensorState;
        this.Type = "BinarySensorState";
        this.Data = state.state as any;
    }
}

export class BinarySensorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: BinarySensorStateEnum = BinarySensorStateEnum.Low;
    Time: string = "";
}

export enum BinarySensorStateEnum {
    High = 1,
    Low = 0
}

/// ConfigurationState
export class ConfigurationDeviceState extends DeviceState<ConfigurationState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.ConfigurationState;
        this.Type = "ConfigurationState";
        this.Data = state.state as any;
    }
}

export class ConfigurationState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Configuration: ConfigurationStateEnum = ConfigurationStateEnum.Unknown;
    Time: string = "";
}

export enum ConfigurationStateEnum {
    Unknown = 0,
    OngoingConfiguration = 1,
    NotConfigured = 2,
    Configured = 3,
    PartiallyConfigured = 4
}

/// BlindOpenCloseTime
export class BlindOpenCloseTimeDeviceState extends DeviceState<BlindOpenCloseTimeState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.BlindOpenCloseTime;
        this.Type = "BlindOpenCloseTimeState";
        this.Data = state.state as any;
    }
}

export class BlindOpenCloseTimeState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Vibration: VibrationTypeEnum = VibrationTypeEnum.No;
    CloseTime: number = 0;
    OpenTime: number = 0;
    HallCounter: number = 0;
    Time: string = "";
}

/// OvercurrentProtection
export class OvercurrentProtectionDeviceState extends DeviceState<OvercurrentProtectionState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.OvercurrentProtectionState;
        this.Type = "OvercurrentProtectionState";
        this.Data = state.state as any;
    }
}

export class OvercurrentProtectionState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    OverrcurrentDetected: boolean = false;
    Time: string = "";
}

/// OvercurrentProtectionThreshold
export class OvercurrentProtectionThresholdDeviceState extends DeviceState<OvercurrentProtectionThresholdState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.OvercurrentProtectionThreshold;
        this.Type = "OvercurrentProtectionThresholdState";
        this.Data = state.state as any;
    }
}

export class OvercurrentProtectionThresholdState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Current: number = 0;
    Time: string = "";
}

/// FacadePositionDeviceState
export class FacadePositionDeviceState extends DeviceState<FacadePositionState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.FacadePosition;
        this.Type = "FacadePositionState";
        this.Data = state.state as any;
    }
}

export class FacadePositionState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Position: number = 0;
    Tilt: number = 0;
    Time: string = "";
}

export class FacadeTypeDeviceState extends DeviceState<FacadeTypeState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.FacadeType;
        this.Type = "FacadeTypeState";
        this.Data = state.state as any;
    }
}

export class FacadeTypeState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Time: string = "";
    FType: FacadeType = FacadeType.Facade180;
}

/// CurrentWindThreshold
export class CurrentWindThresholdDeviceState extends DeviceState<CurrentWindThresholdState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.CurrentWindThreshold;
        this.Type = "CurrentWindThresholdState";
        this.Data = state.state as any;
    }
}

export class CurrentWindThresholdState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    WindThreshold: WindThresholdStateEnum = WindThresholdStateEnum.Unknown;
    Time: string = "";
}

export enum WindThresholdStateEnum {
    Unknown = 0,
    Below = 1,
    Exceeded = 2,
    Inactive = 3
}

/// CurrentLightThreshold
export class CurrentLightThresholdDeviceState extends DeviceState<CurrentLightThresholdState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.CurrentLightThreshold;
        this.Type = "CurrentLightThresholdState";
        this.Data = state.state as any;
    }
}

export class CurrentLightThresholdState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    LightThreshold: LightThresholdStateEnum = LightThresholdStateEnum.Unknown;
    Value: number = 0;
    Time: string = "";
}

export enum LightThresholdStateEnum {
    Unknown = 0,
    Below = 1,
    Exceeded = 2
}

/// WindSpeed
export class WindSpeedDeviceState extends DeviceState<WindSpeedState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.WindSpeedState;
        this.Type = "WindSpeedState";
        this.Data = state.state as any;
    }
}

export class WindSpeedState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Value: number = 0;
    Time: string = "";
}

//flood sensor
export class FloodSensorDeviceState extends DeviceState<FloodSensorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.FloodSensorState;
        this.Type = "FloodSensorState";
        this.Data = state.state as any;
    }
}

export class FloodSensorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: FloodSensorStates = FloodSensorStates.NoFloodDetected;
    Time: string = "";
}

export enum FloodSensorStates {
    FloodDetected = 1,
    NoFloodDetected = 0
}

export enum HumidityType {
    AirHumidity = 0,
    InsideHumidity = 1,
    OutsideHumidity = 2,
    SafetyHumidity = 3,
    SetHumidity = 4,
}

//Pressure
export class PressureSensorDeviceState extends DeviceState<PressureSensorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.PressureSensorState;
        this.Type = "PressureSensorState";
        this.Data = state.state as any;
    }
}

export class PressureSensorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    Pressure: number = 0;
    Time: string = "";
    PressureType: PressureType = PressureType.OutsidePressure
}

export enum PressureType {
    InsidePressure = 0,
    OutsidePressure = 1,
    SetPressure = 2
}

export enum FacadeType
{
    Facade90 = 0,
    Facade180 = 1
}

// TamperProtection
export class TamperProtectionDeviceState extends DeviceState<TamperProtectionState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.TamperProtectionState;
        this.Type = "TamperProtection";
        this.Data = state.state as any;
    }
}

export class TamperProtectionState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    State: TamperProtectionStatus = TamperProtectionStatus.OK;
    Time: string = "";
}

export enum TamperProtectionStatus {
    OK = 0,
    Breach = 1
}

//LightRGB
export class LightRGBWDeviceState extends DeviceState<LightRGBWState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.LightRGBWState;
        this.Type = "LightRGBW";
        this.Data = state.state as any;
    }
}

export class LightRGBWState implements IDeviceStateData {
    StateReliability: StateReliability = StateReliability.Confident;
    Time: string = "";
    R: number = 0;
    G: number = 0;
    B: number = 0;
    W: number = 0;
    Channel: number = 0;
    Brightness: number = 0;
}


//BlindError
export class BlindErrorDeviceState extends DeviceState<BlindErrorState> {
    constructor(state: DeviceStateChangedObject) {
        super();
        this.TypeAsEnum = DeviceResponseType.BlindErrorState;
        this.Type = "BlindError";
        this.Data = state.state as any;
    }
}

export class BlindErrorState implements IDeviceStateData {
    Channel: number = 0;
    StateReliability: StateReliability = StateReliability.Unconfident;
    ErrorCode: BlindErrorCode = BlindErrorCode.Unknown;
    ErrorData: Map<string, string> = new Map<string, string>();
    Reset: boolean = false;
    Time: string = "";
}

export enum BlindErrorCode
{
    Unknown = 0,
    ObstacleDetected = 1,
    MotorFailure = 2,
}